import { Box } from '@sequencehq/core-components'
import { Global } from '@emotion/react'
import { usePrefersReducedMotion } from '@sequencehq/utils'
import { FC } from 'react'
import styled, { keyframes } from 'styled-components'

// The pulse originates from the toggle, which sits in the top-right of the card.
const ORIGIN = 'calc(100% - 32px) 32px'

// A hollow ring whose size is animated — growing it reads as an expanding shock-wave.
const PULSE_MASK = `radial-gradient(var(--labs-ring-w) var(--labs-ring-h) at ${ORIGIN}, rgb(0 0 0 / 0%) 20%, rgb(0 0 0 / 100%) 40%, rgb(0 0 0 / 100%) 50%, rgb(0 0 0 / 0%) 75%)`

// Two-tone shock-wave tuning. Ring sizes are a % of the card (0 = grows from a
// point); the colours are the inner/outer tones of the expanding ring.
const PULSE_COLOR_A = '#7fb0ea'
const PULSE_COLOR_B = '#8fe0ea'
const RING_END_W = 80
const RING_END_H = 305
const PULSE_DURATION = 1.4

const PULSE_BACKGROUND = `
  radial-gradient(circle at ${ORIGIN}, ${PULSE_COLOR_B} 10%, transparent 66%),
  conic-gradient(from var(--labs-conic-rotate) at ${ORIGIN}, ${PULSE_COLOR_A} 15%, ${PULSE_COLOR_B} 57%, ${PULSE_COLOR_A} 100%)
`

// Ring sizes live inside the keyframes. The width eases from 0 → ringEndW with
// the 70% stop sitting 78% of the way along that range, keeping the original
// "width lags height" feel.
const PULSE_KEYFRAMES = keyframes`
  0% { opacity: 0; --labs-ring-w: 0%; --labs-ring-h: 0%; --labs-conic-rotate: -100deg; }
  15% { opacity: 1; }
  70% { --labs-ring-w: ${RING_END_W * 0.78}%; --labs-ring-h: ${RING_END_H}%; }
  100% { opacity: 0; --labs-ring-w: ${RING_END_W}%; --labs-ring-h: ${RING_END_H}%; --labs-conic-rotate: 50deg; }
`

const PulseRing = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${PULSE_BACKGROUND};
  background-blend-mode: color;
  mask-image: ${PULSE_MASK};
  -webkit-mask-image: ${PULSE_MASK};
  mask-size: cover;
  -webkit-mask-size: cover;
  mask-position: center;
  -webkit-mask-position: center;
  mask-repeat: no-repeat;
  -webkit-mask-repeat: no-repeat;
  animation: ${PULSE_KEYFRAMES} ${PULSE_DURATION}s
    cubic-bezier(0.278, 0.001, 0.393, 1) both;
`

/**
 * One-shot activation flourish played when Sequence Labs is enabled: a two-tone
 * shock-wave ring expands from the toggle and fades out. Adapted from the
 * "Gradient Pulse" CodePen. The border glow is drawn separately by
 * {@link LabsBorderBeam}. Remount it (via a changing `key`) to replay.
 *
 * Rendered as an absolutely-positioned card overlay — the parent owns positioning
 * by giving the card `position="relative"`.
 */
export const LabsActivationPulse: FC = () => {
  const prefersReducedMotion = usePrefersReducedMotion()

  if (prefersReducedMotion) return null

  return (
    <Box
      position="absolute"
      inset={0}
      borderRadius="inherit"
      pointerEvents="none"
      aria-hidden
      zIndex={0}
      data-testid="labs.activationPulse"
    >
      {/* Typed custom properties so they can be animated. */}
      <Global
        styles={`
          @property --labs-ring-w {
            syntax: '<percentage>';
            inherits: false;
            initial-value: 0%;
          }
          @property --labs-ring-h {
            syntax: '<percentage>';
            inherits: false;
            initial-value: 0%;
          }
          @property --labs-conic-rotate {
            syntax: '<angle>';
            inherits: false;
            initial-value: -100deg;
          }
        `}
      />

      {/* Shock-wave ring expanding from the toggle, clipped to the card. */}
      <Box
        position="absolute"
        inset={0}
        borderRadius="inherit"
        overflow="hidden"
      >
        <PulseRing />
      </Box>
    </Box>
  )
}
